import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import FloatingWhatsApp from '@/components/feature/FloatingWhatsApp';
import ButtonCTA from '@/components/base/ButtonCTA';
import ButtonWhatsApp from '@/components/base/ButtonWhatsApp';
import ButtonPhone from '@/components/base/ButtonPhone';
import ReassuranceBar from '@/components/feature/ReassuranceBar';
import ServiceCard from '@/components/feature/ServiceCard';
import ReviewsBlock from '@/components/feature/ReviewsBlock';
import CTABlock from '@/components/feature/CTABlock';
import ContactFormBlock from '@/components/feature/ContactFormBlock';
import RevealWrapper from '@/components/base/RevealWrapper';
import { SERVICES, BLOG_POSTS, PRICING_TEASER, REASSURANCE_ITEMS, HOW_IT_WORKS_STEPS, WHY_GWADA_TEXT } from '@/mocks/homeData';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { localBusiness, website, service, faqPage, breadcrumb, article } from '@/lib/seo';

export default function Home() {
  useDocumentHead({
    title: 'Gwada Nuisibles : dératisation & désinsectisation Guadeloupe',
    description: 'Rats, ravets, termites, punaises : nos techniciens certifiés interviennent à domicile partout en Guadeloupe. Devis en 2 h, prix ferme.',
    jsonLd: [localBusiness(), website()],
  });

  return (
    <main>
      <Header />

      {/* HERO */}
      <section className="relative bg-primary-500 overflow-hidden min-h-[560px] md:min-h-[640px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="/images/home-1.webp"
            alt="Technicien en combinaison de protection pulvérisant un insecticide à l'intérieur d'une maison créole en Guadeloupe"
            className="w-full h-full object-cover object-center"
           loading="eager" fetchPriority="high" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(11,58,46,0.94), rgba(11,58,46,0.74) 52%, rgba(11,58,46,0.42))' }} />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(11,58,46,0.55), transparent 38%, transparent 82%, rgba(11,58,46,0.38))' }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24 w-full">
          <div className="max-w-3xl">
            <h1 className="font-heading font-black text-3xl md:text-5xl text-white leading-tight mb-6">
              Rats, ravets, termites&nbsp;: on s&rsquo;en occupe, partout en Guadeloupe.
            </h1>
            <p className="text-white/85 text-base md:text-lg leading-relaxed mb-8 max-w-2xl">
              Gwada Nuisibles intervient à domicile sur toute l&rsquo;île : dératisation, désinsectisation, traitement termites et punaises de lit. Diagnostic par WhatsApp en 2 h, prix ferme annoncé avant intervention.
            </p>
            <div className="flex flex-col sm:flex-row items-start gap-4 flex-wrap">
              <ButtonCTA>Devis gratuit</ButtonCTA>
              <ButtonWhatsApp>WhatsApp direct</ButtonWhatsApp>
              <ButtonPhone />
            </div>
          </div>
        </div>
      </section>

      {/* RÉASSURANCE */}
      <ReassuranceBar items={REASSURANCE_ITEMS} />

      {/* NOS INTERVENTIONS */}
      <section className="bg-background-100 py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-4">
                Nos interventions
              </h2>
              <p className="text-foreground-600 text-base leading-relaxed max-w-3xl mx-auto">
                Un nuisible ne disparaît jamais tout seul sous notre climat. Chaleur toute l&rsquo;année, humidité de la saison des pluies&nbsp;: ce qui commence par &laquo;&nbsp;j&rsquo;ai vu un ravet dans la cuisine&nbsp;&raquo; finit en colonie installée. Nos techniciens traitent le problème à la racine — et vous expliquent comment éviter qu&rsquo;il revienne.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop>
              {SERVICES.map((service) => (
                <ServiceCard
                  key={service.link}
                  title={service.title}
                  description={service.description}
                  link={service.link}
                  imageUrl={service.imageUrl}
                  imageAlt={service.imageAlt}
                />
              ))}
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* COMMENT ÇA SE PASSE */}
      <section className="bg-white py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-4">
                Comment ça se passe
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
              {HOW_IT_WORKS_STEPS.map((step) => (
                <div key={step.number} className="text-center">
                  <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center rounded-full bg-secondary-100">
                    <i className={`${step.icon} text-secondary-500 text-2xl w-7 h-7 flex items-center justify-center`}></i>
                  </div>
                  <div className="w-8 h-8 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-500 text-white font-heading font-bold text-sm">
                    {step.number}
                  </div>
                  <h3 className="font-heading font-bold text-lg text-foreground-950 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* POURQUOI GWADA NUISIBLES */}
      <section className="bg-secondary-500 py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
              <div>
                <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-6">
                  Pourquoi Gwada Nuisibles
                </h2>
                <p className="text-white/90 text-base leading-relaxed">
                  {WHY_GWADA_TEXT}
                </p>
              </div>
              <div className="relative">
                <div className="w-full h-72 md:h-96 rounded-xl overflow-hidden">
                  <img
                    src="/images/home-2.webp"
                    alt="Technicien certifié Certibiocide en uniforme devant une maison créole avec jardin tropical en Guadeloupe"
                    className="w-full h-full object-cover"
                   loading="lazy" decoding="async" />
                </div>
              </div>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* NOS TARIFS EN CLAIR */}
      <section className="bg-background-100 py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-4">
                Nos tarifs en clair
              </h2>
            </div>
            <div className="max-w-lg mx-auto">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                <div className="bg-white rounded-xl p-6 text-center border border-background-300/60">
                  <p className="text-foreground-500 text-xs uppercase tracking-wider mb-2">Dératisation</p>
                  <p className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
                    dès {PRICING_TEASER.deratisation}
                  </p>
                </div>
                <div className="bg-white rounded-xl p-6 text-center border border-background-300/60">
                  <p className="text-foreground-500 text-xs uppercase tracking-wider mb-2">Ravets</p>
                  <p className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
                    dès {PRICING_TEASER.ravets}
                  </p>
                </div>
                <div className="bg-white rounded-xl p-6 text-center border border-background-300/60">
                  <p className="text-foreground-500 text-xs uppercase tracking-wider mb-2">Termites</p>
                  <p className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
                    {PRICING_TEASER.termites}
                  </p>
                </div>
              </div>
              <div className="text-center">
                <Link
                  to="/tarifs"
                  className="inline-flex items-center gap-2 text-secondary-500 font-semibold hover:text-secondary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Voir tous les tarifs
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </Link>
              </div>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* CONSEILS / BLOGUE */}
      <section className="bg-white py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-4">
                Conseils
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {BLOG_POSTS.map((post) => (
                <Link
                  key={post.link}
                  to={post.link}
                  className="group bg-background-100 rounded-xl overflow-hidden transition-all duration-300 hover:translate-y-[-2px] cursor-pointer border border-background-200/70"
                >
                  <div className="w-full h-48 overflow-hidden">
                    <img
                      src={post.imageUrl}
                      alt={post.imageAlt}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                     loading="lazy" decoding="async" />
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading font-bold text-lg text-foreground-950 mb-2 group-hover:text-secondary-500 transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-foreground-600 text-sm leading-relaxed">
                      {post.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* AVIS */}
      <section className="bg-background-100 py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-4">
                Avis clients
              </h2>
            </div>
            <ReviewsBlock />
          </div>
        </RevealWrapper>
      </section>

      {/* CTA FINAL */}
      <CTABlock />

      <ContactFormBlock source="accueil" />

      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}